import React, { useEffect, useState } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import HeaderBar from "../components/HeaderBar";
import MenuBar from "../components/MenuBar";
import CustomerEntryFormTab from "./customerEntry/CustomerEntryFormTab";
import CustomerEntryReportTab from "./customerEntry/CustomerEntryReportTab";
import CustomerEntryDetailPage from "./customerEntry/CustomerEntryDetailPage";
import CustomerMonthlyPurchaseReportPage from "./customerEntry/CustomerMonthlyPurchaseReportPage";
import CustomerGiftSettingsPage from "./customerEntry/CustomerGiftSettingsPage";
import { CE_DRAFT_KEY } from "./customerEntry/customerEntryDraft";
import toast, { Toaster } from "react-hot-toast";

function CustomerEntryLayout() {
  const [entryModalOpen, setEntryModalOpen] = useState(false);
  const [refresh, setRefresh] = useState(0);
  /** Remounts the new-entry form: clears all fields and draft (used by Refresh). */
  const [formResetKey, setFormResetKey] = useState(0);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") setEntryModalOpen(false);
    };
    if (entryModalOpen) {
      document.addEventListener("keydown", onKey);
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "";
    }
    return () => {
      document.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [entryModalOpen]);

  // Global shortcuts: N = new entry, / = focus search (when not typing in an input)
  useEffect(() => {
    const onShortcut = (e) => {
      if (entryModalOpen) return;
      const t = e.target;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA" || t.tagName === "SELECT")) return;
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      if (e.key === "n" || e.key === "N") {
        e.preventDefault();
        setEntryModalOpen(true);
      }
      if (e.key === "/") {
        e.preventDefault();
        document.getElementById("ce-search-input")?.focus();
      }
    };
    window.addEventListener("keydown", onShortcut);
    return () => window.removeEventListener("keydown", onShortcut);
  }, [entryModalOpen]);

  const handleSaved = () => {
    setRefresh((r) => r + 1);
    setEntryModalOpen(false);
  };

  const handleFormRefresh = () => {
    try {
      localStorage.removeItem(CE_DRAFT_KEY);
    } catch {
      /* ignore */
    }
    setFormResetKey((k) => k + 1);
    toast.success("Form cleared");
  };

  return (
    <div className="flex min-h-screen flex-col bg-gradient-to-b from-slate-50 via-gray-50 to-slate-100">
      <Toaster position="top-center" toastOptions={{ duration: 3200 }} />
      <MenuBar />
      <HeaderBar />
      <div className="flex min-h-0 w-full flex-1 flex-col px-3 pb-28 pt-3 sm:px-5 lg:px-8 xl:px-10">
        <div className="mb-2 flex shrink-0 flex-wrap items-center justify-between gap-2">
          <h1 className="text-base font-semibold tracking-tight text-gray-900">Customer entry</h1>
        </div>

        <CustomerEntryReportTab onNewEntry={() => setEntryModalOpen(true)} />
      </div>

      {entryModalOpen && (
        <div
          className="fixed inset-0 z-[1200] flex items-center justify-center p-4 sm:p-6"
          role="dialog"
          aria-modal="true"
          aria-labelledby="customer-entry-modal-title"
        >
          <button
            type="button"
            className="absolute inset-0 bg-gray-900/40 backdrop-blur-sm"
            aria-label="Close dialog"
            onClick={() => setEntryModalOpen(false)}
          />
          <div className="relative z-10 flex max-h-[min(92vh,900px)] w-full max-w-lg flex-col overflow-hidden rounded-2xl bg-white shadow-2xl ring-1 ring-gray-200/80">
            <div className="flex shrink-0 items-center justify-between gap-2 border-b border-gray-100 bg-gradient-to-r from-indigo-50/90 to-white px-4 py-3 sm:px-5">
              <div className="min-w-0 flex-1">
                <h2 id="customer-entry-modal-title" className="text-base font-semibold text-gray-900">
                  New customer entry
                </h2>
                <p className="text-[11px] text-gray-500">Fill details below, then save.</p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  onClick={handleFormRefresh}
                  className="inline-flex items-center gap-1.5 rounded-lg border border-indigo-200/80 bg-white px-3 py-1.5 text-xs font-semibold text-indigo-700 shadow-sm transition hover:bg-indigo-50"
                  title="Clear all fields and start a new entry (saved invoices are still checked for duplicates)"
                >
                  <svg className="h-4 w-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden>
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                    />
                  </svg>
                  Refresh
                </button>
                <button
                  type="button"
                  onClick={() => setEntryModalOpen(false)}
                  className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800"
                  aria-label="Close"
                >
                  <svg className="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            </div>
            <div className="min-h-0 flex-1 overflow-y-auto overscroll-contain px-4 py-4 sm:px-5 sm:py-5">
              <CustomerEntryFormTab
                key={`${refresh}-${formResetKey}`}
                onSaved={handleSaved}
                variant="modal"
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default function CustomerEntryPage() {
  return (
    <Routes>
      <Route index element={<CustomerEntryLayout />} />
      <Route path="monthly" element={<CustomerMonthlyPurchaseReportPage />} />
      <Route path="gifts" element={<CustomerGiftSettingsPage />} />
      <Route path="detail/:customerName" element={<CustomerEntryDetailPage />} />
      <Route path="*" element={<Navigate to="/reports/customer-entry" replace />} />
    </Routes>
  );
}
